import { createPanelComponent } from '@modules/src/panel/createPanelComponent'
import { IComponent, ICustomEventData, IEventSettings } from '@modules/src/interfaces/IComponents'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ButtonState } from '@modules/src/enums/ButonStates'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { syncPanelButtonEnabledState } from '@modules/src/panel/PanelUtils'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import GameSignals from '../../signals/GameSignals'
import Facade from '../../Facade'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { resolvePromoWinAmount } from '@/managers/promo/PromoSessionManager'
import { isBuyBonusHidden } from '@/managers/ui/BuyBonusManager'
import { isTabletViewport } from '@/utils/DeviceLayout'
import { registerTournamentBadgeAnchor } from '@/ui/TournamentBadgeAnchor'

//## Match reel frame / logo / board fades during base → feature intro.
const PANEL_FEATURE_INTRO_FADE_OUT_S = 0.35
const PANEL_FEATURE_INTRO_FADE_IN_S = 0.25

const fadeOutPanelForFeatureIntro = (data: ICustomEventData): void => {
    const element = data.component?.componentElement as PIXI.Container | undefined
    if (!element || element.destroyed) {
        return
    }

    gsap.killTweensOf(element)

    if (!element.visible) {
        element.alpha = 1
        return
    }

    gsap.to(element, {
        duration: PANEL_FEATURE_INTRO_FADE_OUT_S,
        alpha: 0,
        onComplete: () => {
            element.visible = false
            element.alpha = 1
        },
    })
}

const fadeInPanelAfterFeatureIntro = (data: ICustomEventData): void => {
    const element = data.component?.componentElement as PIXI.Container | undefined
    if (!element || element.destroyed) {
        return
    }

    gsap.killTweensOf(element)
    element.visible = true
    element.alpha = 0
    gsap.to(element, {
        duration: PANEL_FEATURE_INTRO_FADE_IN_S,
        alpha: 1,
    })
}

// Extend the live Facade singleton via prototype chain so F.model stays reactive.
// Object.create keeps all property reads live (including model which starts null).
const panelFacade = Object.create(Facade)
Object.defineProperty(panelFacade, 'currencySymbol', {
    get() { return (Facade as any).model?.initData?.player?.currency ?? '' },
    enumerable: true,
    configurable: true,
})

const isMobileLandscapeLayout = (): boolean => DeviceUtils.isMobileDevice()

/** Desktop landscape — spin cluster bottom-center (current). */
const DESKTOP_LANDSCAPE = {
    spin: { x: 945.5, y: 826, scale: 0.9 },
    quick: { x: 847.5, y: 946.5, scale: 0.9 },
    auto: { x: 1158.5, y: 946.5, scale: 0.9 },
    balance: { x: 31, y: 952 },
    win: { x: 347, y: 952 },
    stake: { x: 1447.5, y: 952 },
    buyBonus: { x: 1698.5, y: 860.5, scale: 0.7 },
    autoplayIndicator: { x: 1012, y: 931 },
} as const

/**
 * Mobile landscape — spin / turbo / auto stacked on the right;
 * stake +/- centered where the desktop spin button sits.
 */
const MOBILE_LANDSCAPE = {
    spin: { x: 1709 - 38, y: 394, scale: 0.85 },
    quick: { x: 1770 - 38, y: 315, scale: 0.7 },
    auto: { x: 1770 - 38, y: 647.5, scale: 0.7 },
    balance: { x: 31, y: 979.5 },
    win: { x: 347, y: 979.5 },
    stake: { x: 960, y: 979.5 },
    buyBonus: { x: 1674, y: 884.5, scale: 0.6 },
    // Anchored to spin (1709,394) @ 0.85 — ∞ slot; digits re-centered in PanelUtils.
    autoplayIndicator: { x: 1775.5, y: 494 },
} as const

const landscapeLayout = () => (isMobileLandscapeLayout() ? MOBILE_LANDSCAPE : DESKTOP_LANDSCAPE)

/**
 * Portrait menu button. Tablets use the 96px menu_settings_ art, so it is sized and baselined
 * exactly like Fast Play / Autoplay (scale 1, y 1537.5); phones keep the larger menuIcon1_ art.
 */
const menuSettingsPortraitLayout = () =>
    isTabletViewport() ? { x: 4, y: 1537.5, scale: 1 } : { x: -5, y: 1524.5, scale: 0.8 }

//## Top-right HUD row (Home / Sound / Menu Settings), factory 1708 / 1780 / 1850 at y:30
//## landscape and 812 / 951 at y:40 portrait. Every rule shifts the whole row, keeping the spacing.
//##
//## Contain-fit never fills the viewport on both axes: a viewport taller than the world leaves a
//## band above it, a wider one leaves a band beside it. The row has to hug the device edge, not
//## the world edge, so the band is measured and folded into the offsets. The hand-tuned values
//## this replaces (tablet 1843 / -150 and 1117 / 17, phone 2075.5 and -202) were each the band of
//## one specific device, which is why they broke elsewhere — most visibly on a phone, where the
//## Safari chrome shrinks the viewport enough to collapse the band the fixed y relied on.
const MOBILE_TOP_ROW_INSET = 17
const MOBILE_LANDSCAPE_TOP_RIGHT_SHIFT = 1843 - 1850

//## The factory x leaves the icon ~10 design px short of the world edge, against the 17 the inset
//## gives it vertically. Pull it back so both edges keep a comparable, deliberate gap.
const MOBILE_TOP_ROW_EDGE_SAFETY = 10

const WORLD_FALLBACK: Record<string, { width: number; height: number }> = {
    [Orientation.LANDSCAPE]: { width: 1920, height: 1080 },
    [Orientation.PORTRAIT]: { width: 1080, height: 1920 },
}

/** Background visible beyond the world on each side, in design pixels. Only one axis is ever > 0. */
const resolveViewportBands = (orientation: Orientation): { top: number; side: number } => {
    const viewportWidth = window.visualViewport?.width || window.innerWidth || 0
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 0

    const fallback = WORLD_FALLBACK[orientation] || WORLD_FALLBACK[Orientation.LANDSCAPE]
    const world = (Facade as any).displayConfig?.sizes?.[orientation]
    const worldWidth = Number(world?.WORLD_WIDTH) || fallback.width
    const worldHeight = Number(world?.WORLD_HEIGHT) || fallback.height

    const containScale = Math.min(viewportWidth / worldWidth, viewportHeight / worldHeight)
    if (!(containScale > 0)) {
        return { top: 0, side: 0 }
    }

    return {
        top: Math.max(0, (viewportHeight - worldHeight * containScale) / 2) / containScale,
        side: Math.max(0, (viewportWidth - worldWidth * containScale) / 2) / containScale,
    }
}

const roundToTenth = (value: number): number => Math.round(value * 10) / 10

const mobileTopRight = (baseX: number, orientation: Orientation): { x: number; y: number } => {
    const { top, side } = resolveViewportBands(orientation)
    const shift = orientation === Orientation.LANDSCAPE ? MOBILE_LANDSCAPE_TOP_RIGHT_SHIFT : 0

    return {
        x: roundToTenth(baseX + shift + side - MOBILE_TOP_ROW_EDGE_SAFETY),
        y: roundToTenth(MOBILE_TOP_ROW_INSET - top),
    }
}

//## Same band as the top-right row, mirrored: this button hugs the left edge, so the band is
//## subtracted instead of added. On an iPhone in Safari the portrait band is never zero (7-32 CSS
//## px depending on the model), which is the gap the button showed against the device edge while
//## the top-right row already compensated for it. Tablets keep the hand-tuned x: their band is an
//## order of magnitude wider and the letterboxed world reads as the game area there.
const menuSettingsPortraitPosition = (): { x: number; y: number } => {
    const { x, y } = menuSettingsPortraitLayout()

    if (isTabletViewport() || !DeviceUtils.isMobileDevice()) {
        return { x, y }
    }

    return { x: roundToTenth(x - resolveViewportBands(Orientation.PORTRAIT).side), y }
}

const syncMenuSettingsPortraitPosition = (data: ICustomEventData): void => {
    const component = data.component as any
    const element = component?.componentElement as PIXI.Container | undefined

    if (!element || element.destroyed || ModulesFacade.orientation !== Orientation.PORTRAIT) {
        return
    }

    const { x, y } = menuSettingsPortraitPosition()
    const positions = component.settings?.position as { orientation: Orientation; x: number; y: number }[] | undefined
    const current = positions?.find((entry) => entry.orientation === Orientation.PORTRAIT)

    if (current) {
        current.x = x
        current.y = y
    }

    element.position.set(x, y)
}

const topRightLandscape = (baseX: number): { x: number; y: number } =>
    isMobileLandscapeLayout() ? mobileTopRight(baseX, Orientation.LANDSCAPE) : { x: baseX, y: 30 }

const topRightPortrait = (baseX: number): { x: number; y: number } =>
    DeviceUtils.isMobileDevice() ? mobileTopRight(baseX, Orientation.PORTRAIT) : { x: baseX, y: 40 }

/** Factory x per orientation. Portrait Menu Settings is omitted: it sits in the bottom row instead. */
type TopRightRowBase = { landscape: number; portrait?: number }

//## The patch map is built once at import time, so the bands have to be re-measured whenever the
//## viewport changes — otherwise a rotation keeps the offsets resolved for the other aspect.
const syncTopRightRowPosition = (data: ICustomEventData, base: TopRightRowBase): void => {
    const component = data.component as any
    const element = component?.componentElement as PIXI.Container | undefined

    if (!element || element.destroyed || !DeviceUtils.isMobileDevice()) {
        return
    }

    const orientation = ModulesFacade.orientation as Orientation
    const baseX = orientation === Orientation.LANDSCAPE ? base.landscape : base.portrait

    if (typeof baseX !== 'number') {
        return
    }

    const { x, y } = mobileTopRight(baseX, orientation)
    const positions = component.settings?.position as { orientation: Orientation; x: number; y: number }[] | undefined
    const current = positions?.find((entry) => entry.orientation === orientation)

    if (current) {
        current.x = x
        current.y = y
    }

    element.position.set(x, y)
}

const topRightRowEvents = (base: TopRightRowBase): IEventSettings[] => [
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.completeSplashScreenState, ModuleSignals.gameResized],
        callback: (data: ICustomEventData) => syncTopRightRowPosition(data, base),
    } as IEventSettings,
]

//## The tournament badge is HTML and lives outside the canvas, so it can only sit under this row
//## by measuring it. Registered on the same beats the row is positioned on.
const badgeAnchorEvents = (): IEventSettings[] => [
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.completeSplashScreenState, ModuleSignals.gameResized],
        callback: (data: ICustomEventData) =>
            registerTournamentBadgeAnchor((data.component as any)?.componentElement as PIXI.Container | undefined),
    } as IEventSettings,
]

//## IPanelConfig has no itemPatches hook, so game-specific layout is applied to the tree the
//## factory returns. Both orientations are declared in full: patching replaces the whole
//## position/scale array, so landscape has to be restated even where it matches the factory.
const PANEL_ITEM_PATCHES = new Map<string, Partial<IComponent>>([
    [
        'Spin Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().spin.x, y: landscapeLayout().spin.y },
                { orientation: Orientation.PORTRAIT, x: 406.5, y: 1386 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().spin.scale, y: landscapeLayout().spin.scale },
                { orientation: Orientation.PORTRAIT, x: 1.2, y: 1.2 },
            ],
        },
    ],
    [
        'Quick Spin Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().quick.x, y: landscapeLayout().quick.y },
                { orientation: Orientation.PORTRAIT, x: 301.5, y: 1537.5 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().quick.scale, y: landscapeLayout().quick.scale },
                { orientation: Orientation.PORTRAIT, x: 1, y: 1 },
            ],
        },
    ],
    [
        'Autoplay Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().auto.x, y: landscapeLayout().auto.y },
                { orientation: Orientation.PORTRAIT, x: 674, y: 1537.5 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().auto.scale, y: landscapeLayout().auto.scale },
                { orientation: Orientation.PORTRAIT, x: 1, y: 1 },
            ],
        },
    ],
    [
        'Balance Container',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...landscapeLayout().balance },
                { orientation: Orientation.PORTRAIT, x: 7.5, y: 1780 },
            ],
        },
    ],
    [
        'Win Container',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...landscapeLayout().win },
                { orientation: Orientation.PORTRAIT, x: 807, y: 1780 },
            ],
        },
    ],
    [
        'Stake Container',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...landscapeLayout().stake },
                { orientation: Orientation.PORTRAIT, x: 490, y: 1780 },
            ],
        },
    ],
    [
        'Buy Bonus Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().buyBonus.x, y: landscapeLayout().buyBonus.y },
                { orientation: Orientation.PORTRAIT, x: 840, y: 1489 },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: landscapeLayout().buyBonus.scale, y: landscapeLayout().buyBonus.scale },
                { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
            ],
        },
    ],
    [
        'Autoplay Infinity Indicator',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...landscapeLayout().autoplayIndicator },
                // Anchored to portrait spin (406.5,1386) @ 1.2 — ∞ slot; digits re-centered in PanelUtils.
                { orientation: Orientation.PORTRAIT, x: 502, y: 1528.5 },
            ],
        },
    ],
    [
        'Home Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...topRightLandscape(1708) },
                { orientation: Orientation.PORTRAIT, ...topRightPortrait(812) },
            ],
        },
    ],
    [
        'Sound Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...topRightLandscape(1780) },
                { orientation: Orientation.PORTRAIT, ...topRightPortrait(951) },
            ],
        },
    ],
    [
        'Menu Settings Button',
        {
            position: [
                { orientation: Orientation.LANDSCAPE, ...topRightLandscape(1850) },
                {
                    orientation: Orientation.PORTRAIT,
                    ...menuSettingsPortraitPosition(),
                },
            ],
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                {
                    orientation: Orientation.PORTRAIT,
                    x: menuSettingsPortraitLayout().scale,
                    y: menuSettingsPortraitLayout().scale,
                },
            ],
        },
    ],
])

//## Menu Icons Has Different for Portrait and Landscape, plus a dedicated tablet portrait art
const MENU_ICON_LANDSCAPE_PREFIX = 'menuIcon_'
const MENU_ICON_PORTRAIT_PREFIX = 'menuIcon1_'
const MENU_ICON_TABLET_PORTRAIT_PREFIX = 'menu_settings_'
const MENU_ICON_PREFIXES = [
    MENU_ICON_TABLET_PORTRAIT_PREFIX,
    MENU_ICON_PORTRAIT_PREFIX,
    MENU_ICON_LANDSCAPE_PREFIX,
]

const resolveMenuIconPrefix = (): string => {
    if (ModulesFacade.orientation !== Orientation.PORTRAIT) {
        return MENU_ICON_LANDSCAPE_PREFIX
    }

    return isTabletViewport() ? MENU_ICON_TABLET_PORTRAIT_PREFIX : MENU_ICON_PORTRAIT_PREFIX
}

//## The panel module rewrites srcs with its own landscape prefix (normal / disabled), so match
//## whichever prefix is currently in place instead of assuming the previous orientation's one.
const toOrientationMenuIcon = (src: string): string => {
    const currentPrefix = MENU_ICON_PREFIXES.find((prefix) => src.includes(prefix))

    return currentPrefix ? src.replace(currentPrefix, resolveMenuIconPrefix()) : src
}

//## syncSettingsButtonState hardcodes menuIcon_normal.png and re-applies it on every panel
//## state change, so this has to run after it rather than instead of it — hence appended
//## events on the same signals. Mirroring whatever src the module just wrote also means the
//## enabled/disabled distinction it applies carries over without tracking panel state here.
//## Must cover every signal the module re-syncs the button on, including the ones raised behind
//## popups (feature award / end, buy bonus, unfinished round). Any signal missing here left the
//## module's landscape art in place until an unrelated one fired, which on a portrait phone reads
//## as the icon shrinking to the round variant while the popup is up and growing back after.
const MENU_ICON_SYNC_SIGNALS = [
    GameSignals.completeSplashScreenState,
    GameSignals.roundStart,
    GameSignals.roundStop,
    GameSignals.lockGamePanel,
    GameSignals.unLockGamePanel,
    GameSignals.startAutoplay,
    GameSignals.stopAutoplay,
    ModuleSignals.gameResized,
    (GameSignals as any).featureIntroRequested,
    (GameSignals as any).featureIntroContinue,
    GameSignals.showFreeSpinsIntro,
    (GameSignals as any).showFreeSpinsBackground,
    (GameSignals as any).bonusStartComplete,
    (GameSignals as any).showFreeSpinsSummary,
    (GameSignals as any).showFreeSpinsSummaryComplete,
    GameSignals.showBaseGameBackground,
    GameSignals.bonusStop,
    (GameSignals as any).holdAndWinStop,
    (GameSignals as any).openBuyBonusPopup,
    (GameSignals as any).buyBonusClicked,
    (GameSignals as any).buyBonusConfirmButtonClicked,
    (GameSignals as any).buyBonusResponse,
    (GameSignals as any).showPopup,
    (GameSignals as any).hidePopup,
].filter(Boolean)

const syncMenuIconOrientation = (data: ICustomEventData): void => {
    const component = data.component as any
    const button = component?.componentElement
    if (!button) return

    const states = component.settings?.buttonStates as { state: ButtonState; src?: string }[] | undefined
    if (!Array.isArray(states)) return

    states.forEach((state) => {
        if (typeof state?.src === 'string') state.src = toOrientationMenuIcon(state.src)
    })

    const restingSrc = states.find((state) => state.state === ButtonState.POINTER_OUT)?.src
    if (restingSrc) button.texture = PIXI.Texture.from(restingSrc)
}

type CircleRatios = { cx: number; cy: number; r: number }

//## Correct for every round icon button: art is a circle inscribed in a square frame.
const INSCRIBED_CIRCLE: CircleRatios = { cx: 0.5, cy: 0.5, r: 0.5 }
const SPIN_BUTTON_CIRCLE: CircleRatios = { cx: 108.5 / 218, cy: 167 / 276, r: 105 / 218 }

//## Hit area is a circle inscribed in the button's square texture.
const applyCircularHitArea = (data: ICustomEventData, ratios: CircleRatios): void => {
    const element = data.component?.componentElement as PIXI.Sprite | undefined
    const texture = element?.texture
    if (!element || !texture?.width || !texture?.height) return

    const anchorX = element.anchor?.x ?? 0
    const anchorY = element.anchor?.y ?? 0

    element.hitArea = new PIXI.Circle(
        texture.width * (ratios.cx - anchorX),
        texture.height * (ratios.cy - anchorY),
        texture.width * ratios.r
    )
}

//## Circular hit area is applied after the module sets the texture, so it has to run on every
const circularHitAreaEvents = (ratios: CircleRatios = INSCRIBED_CIRCLE): IEventSettings[] => [
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.completeSplashScreenState, ModuleSignals.gameResized],
        callback: (data: ICustomEventData) => applyCircularHitArea(data, ratios),
    } as IEventSettings,
]

/** Keep Balance/Win meter pill backgrounds visible in all orientations (same as Stake). */
const syncMeterBackgroundAlpha = (data: ICustomEventData): void => {
    const element = data.component?.componentElement as PIXI.Container | undefined
    if (!element) return

    element.alpha = 1
}

const METER_BACKGROUND_ALPHA_EVENTS: IEventSettings[] = [
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.completeSplashScreenState, ModuleSignals.gameResized],
        callback: syncMeterBackgroundAlpha,
    } as IEventSettings,
]

//## Meter labels are centred on their pill at a fixed 25px, so a long translation runs past the
//## artwork: en_sc renders STAKE as PLAY AMOUNT, 209px against the 203px the stake pill measures in
//## landscape (290px art at 0.7) versus 261px in portrait (0.9) — which is why portrait alone reads
//## correctly. Fit the label to the pill it sits on rather than lowering the font for every locale,
//## so the translations that already fit keep the design size.
const METER_LABEL_WIDTH_RATIO = 0.88
const METER_LABEL_MIN_SCALE = 0.6

const fitMeterLabelToBackground = (data: ICustomEventData, backgroundName: string): void => {
    const label = data.component?.componentElement as PIXI.Text | undefined
    if (!label || label.destroyed) return

    const siblings = (label.parent as PIXI.Container | null)?.children as { name?: string }[] | undefined
    const background = siblings?.find((child) => child?.name === backgroundName) as PIXI.Sprite | undefined
    if (!background) return

    // Measure unscaled: the module never resets our scale, so a rotation would otherwise compound it.
    label.scale.set(1)

    const available = background.width * METER_LABEL_WIDTH_RATIO
    if (!(available > 0) || label.width <= available) return

    label.scale.set(Math.max(METER_LABEL_MIN_SCALE, available / label.width))
}

const meterLabelFitEvents = (backgroundName: string): IEventSettings[] => [
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.completeSplashScreenState, ModuleSignals.gameResized],
        callback: (data: ICustomEventData) => fitMeterLabelToBackground(data, backgroundName),
    } as IEventSettings,
]

/** Buy Bonus only while truly idle in base game — never during spin / commendation / features. */
const isBuyBonusInteractionAllowed = (): boolean => {
    if (isBuyBonusHidden()) {
        return false
    }

    if (Facade.isAuto || Facade.isPopupBlockingInput) {
        return false
    }

    if (Boolean((Facade as any).isBuyBonusPopupOpen) || Boolean(Facade.model?.isBuyBonusPending)) {
        return false
    }

    // Commendation (and FS collect) must finish before Buy Bonus can be opened again.
    if (
        Boolean((Facade as any).isBigWinPresentationActive) ||
        Boolean((Facade as any).isFsCollectPresentationActive)
    ) {
        return false
    }

    if (Boolean(Facade.model?.roundState?.isFreeSpins) || Boolean(Facade.model?.spinState?.isHoldAndWinActive)) {
        return false
    }

    return Facade.currentState === GameStatesEnum.IDLE_STATE
}

const disablePanelBuyBonusButton = (button: PIXI.Sprite): void => {
    button.visible = false
    button.interactive = false
    button.cursor = 'default'
    button.alpha = 1
    if ('eventMode' in button) {
        ;(button as any).eventMode = 'none'
    }
}

const syncPanelBuyBonusButton = (data: ICustomEventData): void => {
    const button = data.component.componentElement as PIXI.Sprite | undefined
    if (!button) return

    if (!isBuyBonusInteractionAllowed()) {
        disablePanelBuyBonusButton(button)
        return
    }

    button.visible = true
    syncPanelButtonEnabledState(data, true, 'buyfeature.png', 'buyfeature.png')
}

//## Appended after the factory's own handlers so they run last for a given signal —
//## Component.setupEvents connects in array order.
const PANEL_APPENDED_EVENTS = new Map<string, IEventSettings[]>([
    [
        'PANEL_CONTAINER',
        [
            {
                // Fade with reel/logo (0.35s) — do not instant-hide on showFreeSpinsIntro
                // or the panel vanishes before the rest of the board.
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
                callback: fadeOutPanelForFeatureIntro,
            } as IEventSettings,
            {
                // Fade back in with reel/logo (0.25s) when the player continues.
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                callback: fadeInPanelAfterFeatureIntro,
            } as IEventSettings,
        ],
    ],
    [
        'Menu Settings Button',
        [
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: MENU_ICON_SYNC_SIGNALS,
                callback: syncMenuIconOrientation,
            } as IEventSettings,
            {
                //## The patch map is built at import time, so the band has to be re-measured on
                //## every viewport change, exactly as the top-right row does for landscape.
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [GameSignals.completeSplashScreenState, ModuleSignals.gameResized],
                callback: syncMenuSettingsPortraitPosition,
            } as IEventSettings,

            ...topRightRowEvents({ landscape: 1850 }),
            ...circularHitAreaEvents(),
        ],
    ],
    [
        'Win Value',
        [
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [GameSignals.startSpin],
                callback: () => {
                    if (!Facade.model?.isPromoSpinsRound?.()) {
                        return
                    }

                    const winAmount = resolvePromoWinAmount(Facade.model?.spinData?.promo_details)
                    if (!(winAmount > 0)) {
                        return
                    }

                    ;(GameSignals as any).setWinPanelValue?.emit(winAmount)
                },
            } as IEventSettings,
        ],
    ],
    ['Balance Background', METER_BACKGROUND_ALPHA_EVENTS],
    ['Win Background', METER_BACKGROUND_ALPHA_EVENTS],
    ['Stake Label', meterLabelFitEvents('Stake Background')],
    ['Balance Label', meterLabelFitEvents('Balance Background')],
    ['Win Label', meterLabelFitEvents('Win Background')],
    ['Bet Up Button', circularHitAreaEvents()],
    ['Bet Down Button', circularHitAreaEvents()],
    [
        'Buy Bonus Button',
        [
            {
                event: ComponentEvent.CUSTOM_NEW,
                onGameSignalsTriggered: [
                    GameSignals.completeSplashScreenState,
                    GameSignals.betCooldownEnded,
                    GameSignals.unLockGamePanel,
                    GameSignals.lockGamePanel,
                    GameSignals.startSpin,
                    GameSignals.stopAutoplay,
                    GameSignals.roundStop,
                    GameSignals.roundStart,
                    GameSignals.idle,
                    GameSignals.showBaseGameBackground,
                    GameSignals.finalState,
                    GameSignals.allReelsStopped,
                    GameSignals.bigWinPresentation,
                    GameSignals.showBigWinComplete,
                    GameSignals.hidePopup,
                    // Fast Play (and other UI dialogs) pause → Idle unlocks the panel while
                    // isPopupBlockingInput is still true, which disables Buy Bonus. Re-sync
                    // on resume so No/Yes restore a clickable button.
                    GameSignals.gameResume,
                    GameSignals.buyBonusResponse,
                    GameSignals.balanceHasChanged,
                    GameSignals.betHasChanged,
                ],
                callback: syncPanelBuyBonusButton,
            } as IEventSettings,
            ...circularHitAreaEvents(),
        ],
    ],
    ['Home Button', topRightRowEvents({ landscape: 1708, portrait: 812 })],
    [
        'Sound Button',
        [
            ...circularHitAreaEvents(),
            ...topRightRowEvents({ landscape: 1780, portrait: 951 }),
            ...badgeAnchorEvents(),
        ],
    ],
    ['Quick Spin Button', circularHitAreaEvents()],
    ['Autoplay Button', circularHitAreaEvents()],
    ['Spin Button', circularHitAreaEvents(SPIN_BUTTON_CIRCLE)],
])

//## No-op for names the tree does not contain — the panel drops Buy Bonus Button entirely
//## when the operator sets hideBuyBonus.
const applyPanelItemPatches = (component: IComponent): IComponent => {
    const patch = PANEL_ITEM_PATCHES.get(component.name)
    const appendedEvents = PANEL_APPENDED_EVENTS.get(component.name)

    let patched: IComponent = patch ? { ...component, ...patch } : component

    if (appendedEvents) {
        patched = { ...patched, events: [...(patched.events || []), ...appendedEvents] }
    }

    return patched.items ? { ...patched, items: patched.items.map(applyPanelItemPatches) } : patched
}

export const PANEL_COMPONENT: IComponent = applyPanelItemPatches(
    createPanelComponent({
        signals: GameSignals,
        facade: panelFacade,
        customSignals: {
            // Example: wire game-specific panel signals here.
            // buyBonusClicked: GameSignals.someGameSpecificSignal,
            // gambleClicked: GameSignals.someOtherGameSpecificSignal,
        },
    })
)